import { PredefinedPayloadExtractor } from '../../../redux/list/list-payload-extractor';
import {
  appendNewItem,
  removeItem,
  updateItemByKey,
} from '../../../redux/list/list-update.reducer';
import {
  createDefaultListState,
  createListReducer,
  ListState,
} from '../../../redux/list/list.reducer';
import { CallApiSuccessResponse } from '../../../redux/middlewares/call-api/call-api';
import { Action, Reducer } from '../../../redux/redux.types';
import { Flow, FlowVersion } from '../../../types/flow.types';
import {
  ActivateFlowVersionAdditionalData,
  FLOW_VERSION_ACTION_NAMES,
} from './flow-versions-list.actions';
import {
  DeactivateFlowActionAdditionalData,
  FLOWS_LIST_ACTION_NAMES,
} from './flows-list.actions';

export type FlowsListState = ListState<Flow>;

const defaultState: FlowsListState = createDefaultListState<Flow>();

const listReducer: Reducer<FlowsListState> = createListReducer({
  actionNames: [
    FLOWS_LIST_ACTION_NAMES.GET_START,
    FLOWS_LIST_ACTION_NAMES.GET_SUCCESS,
    FLOWS_LIST_ACTION_NAMES.GET_ERROR,
  ],
  payloadExtractorOptions: PredefinedPayloadExtractor.CallApi,
  key: 'id',
});

export const flowsList = (
  state: FlowsListState = defaultState,
  action: Action,
): FlowsListState => {
  const listState: FlowsListState = listReducer(state, action);

  switch (action.type) {
    case FLOWS_LIST_ACTION_NAMES.CREATE_SUCCESS:
    case FLOWS_LIST_ACTION_NAMES.DUPLICATE_FLOW_SUCCESS: {
      const { responsePayload }: CallApiSuccessResponse<Flow> = action.payload;
      return appendNewItem(listState, responsePayload, 'id');
    }

    case FLOWS_LIST_ACTION_NAMES.RENAME_FLOW_SUCCESS: {
      const { flowId, newName } = action.payload.additionalData;
      return updateItemByKey<Flow>(
        listState,
        (flow) => ({ ...flow, name: newName }),
        flowId,
      );
    }

    case FLOWS_LIST_ACTION_NAMES.REMOVE_FLOW_SUCCESS: {
      const { flowId } = action.payload.additionalData;
      return removeItem(listState, flowId);
    }

    case FLOW_VERSION_ACTION_NAMES.ACTIVATE_VERSION_SUCCESS: {
      const { flowId, version }: ActivateFlowVersionAdditionalData =
        action.payload.additionalData;
      const updatedVersion: FlowVersion = { ...version, active: true };

      return updateItemByKey<Flow>(
        listState,
        (flow) => ({
          ...flow,
          activeRevision: updatedVersion,
          lastActiveRevision: updatedVersion,
        }),
        flowId,
      );
    }

    case FLOWS_LIST_ACTION_NAMES.DEACTIVATE_FLOW_SUCCESS: {
      const { flowId }: DeactivateFlowActionAdditionalData =
        action.payload.additionalData;
      return updateItemByKey<Flow>(
        listState,
        (flow) => ({
          ...flow,
          activeRevision: null,
          lastActiveRevision: flow.activeRevision,
        }),
        flowId,
      );
    }

    default:
      return listState;
  }
};
